import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { 
  Camera, 
  Car, 
  User, 
  MapPin, 
  FileText, 
  CheckCircle2, 
  ArrowRight, 
  ArrowLeft,
  Upload,
  X,
  AlertTriangle,
  Shield,
  Loader2,
  Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';

// Image compression utility for mobile uploads
const compressImage = (file, maxSizeMB = 1) => {
  return new Promise((resolve, reject) => {
    // If file is not an image, return as-is
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    // If file is already small enough, return as-is
    if (file.size <= maxSizeMB * 1024 * 1024) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // Calculate new dimensions (max 1920px on longest side)
        const maxDimension = 1920;
        if (width > height && width > maxDimension) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        } else if (height > maxDimension) {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Compress with quality 0.7
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              console.log(`Compressed ${file.name}: ${(file.size/1024).toFixed(0)}KB -> ${(blob.size/1024).toFixed(0)}KB`);
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          0.7
        );
      };
      img.onerror = () => resolve(file);
      img.src = event.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
};

const STEPS = [
  { id: 1, title: 'Photos', icon: Camera, description: 'Prenez des photos des dommages' },
  { id: 2, title: 'Véhicule', icon: Car, description: 'Informations sur votre véhicule' },
  { id: 3, title: 'Tiers', icon: User, description: 'Coordonnées du tiers identifié' },
  { id: 4, title: 'Incident', icon: MapPin, description: 'Description de l\'incident' },
  { id: 5, title: 'Confirmation', icon: CheckCircle2, description: 'Vérification et envoi' }
];

const NewClaim = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Form data
  const [photosEntreprise, setPhotosEntreprise] = useState([]);
  const [photosTiers, setPhotosTiers] = useState([]);
  const [companyVehicle, setCompanyVehicle] = useState({
    plate: ''
  });
  const [thirdParty, setThirdParty] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    vehicle: {
      plate: ''
    }
  });
  const [incident, setIncident] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    description: '',
    location: {
      address: '',
      city: '',
      postal_code: ''
    },
    notes: ''
  });
  const [noBodilyInjury, setNoBodilyInjury] = useState(false);
  const fileInputEntrepriseRef = useRef(null);
  const fileInputEntrepriseCameraRef = useRef(null);
  const fileInputTiersRef = useRef(null);
  const fileInputTiersCameraRef = useRef(null);

  const handlePhotoUploadEntreprise = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    files.forEach(file => {
      // Vérifier que c'est bien une image
      if (!file.type.startsWith('image/')) {
        console.warn('File is not an image:', file.type);
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotosEntreprise(prev => [...prev, {
          id: Date.now() + Math.random(),
          file,
          preview: reader.result,
          type: 'photo_damage'
        }]);
      };
      reader.onerror = (err) => {
        console.error('Error reading file:', err);
      };
      reader.readAsDataURL(file);
    });
    
    // Reset input pour permettre de re-sélectionner le même fichier
    e.target.value = '';
  };

  const handlePhotoUploadTiers = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    files.forEach(file => {
      // Vérifier que c'est bien une image
      if (!file.type.startsWith('image/')) {
        console.warn('File is not an image:', file.type);
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotosTiers(prev => [...prev, {
          id: Date.now() + Math.random(),
          file,
          preview: reader.result,
          type: 'photo_context'
        }]);
      };
      reader.onerror = (err) => {
        console.error('Error reading file:', err);
      };
      reader.readAsDataURL(file);
    });
    
    // Reset input pour permettre de re-sélectionner le même fichier
    e.target.value = '';
  };

  const removePhotoEntreprise = (id) => {
    setPhotosEntreprise(prev => prev.filter(p => p.id !== id));
  };

  const removePhotoTiers = (id) => {
    setPhotosTiers(prev => prev.filter(p => p.id !== id));
  };

  // Normalize plate format (remove spaces and dashes, keep uppercase)
  const normalizePlate = (plate) => {
    return plate.replace(/[\s-]/g, '').toUpperCase();
  };

  // Validate plate format (accepts with or without dashes: AA123BB or AA-123-BB)
  const isValidPlate = (plate) => {
    if (!plate) return false;
    const normalized = normalizePlate(plate);
    // French plate: 2 letters, 3 digits, 2 letters (new format)
    // Or old format: digits + letters + digits
    const newFormat = /^[A-Z]{2}[0-9]{3}[A-Z]{2}$/;
    const oldFormat = /^[0-9]{1,4}[A-Z]{1,3}[0-9]{1,3}$/;
    return newFormat.test(normalized) || oldFormat.test(normalized) || normalized.length >= 5;
  };

  const validateStep = (step) => {
    setError('');
    const errors = {};
    
    switch (step) {
      case 1:
        if (photosTiers.length === 0) {
          setError('Veuillez ajouter au moins une photo des véhicules accidentés');
          return false;
        }
        break;
      case 2:
        if (!companyVehicle.plate) {
          errors.companyPlate = 'La plaque d\'immatriculation est requise';
        }
        if (Object.keys(errors).length > 0) {
          setFieldErrors(errors);
          setError('Veuillez corriger les champs en rouge');
          return false;
        }
        break;
      case 3:
        if (!thirdParty.first_name) {
          errors.tpFirstName = 'Le prénom est requis';
        }
        if (!thirdParty.last_name) {
          errors.tpLastName = 'Le nom est requis';
        }
        if (!thirdParty.phone) {
          errors.tpPhone = 'Le téléphone est requis';
        }
        if (!thirdParty.email) {
          errors.tpEmail = 'L\'email est requis';
        } else {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(thirdParty.email)) {
            errors.tpEmail = 'L\'adresse email n\'est pas valide';
          }
        }
        if (!thirdParty.vehicle.plate) {
          errors.tpVehiclePlate = 'La plaque du véhicule tiers est requise';
        }
        if (Object.keys(errors).length > 0) {
          setFieldErrors(errors);
          setError('Veuillez corriger les champs en rouge');
          return false;
        }
        break;
      case 4:
        if (!incident.description) {
          errors.incidentDescription = 'La description de l\'incident est requise';
          setFieldErrors(errors);
          setError('Veuillez corriger les champs en rouge');
          return false;
        }
        break;
      case 5:
        if (!noBodilyInjury) {
          setError('Vous devez confirmer l\'absence de dommage corporel');
          return false;
        }
        break;
      default:
        break;
    }
    
    setFieldErrors({});
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setError('');
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;
    
    setLoading(true);
    setError('');

    try {
      // Create claim
      const incidentDateTime = new Date(`${incident.date}T${incident.time}`);
      
      const claimData = {
        incident_date: incidentDateTime.toISOString(),
        incident_description: incident.description,
        location: (incident.location.address || incident.location.city || incident.location.postal_code) 
          ? incident.location 
          : null,
        company_vehicle: companyVehicle,
        third_party: {
          first_name: thirdParty.first_name,
          last_name: thirdParty.last_name,
          email: thirdParty.email,
          phone: thirdParty.phone,
          address: thirdParty.address || null,
          vehicle: thirdParty.vehicle
        },
        no_bodily_injury_confirmed: noBodilyInjury,
        driver_notes: incident.notes || null
      };

      console.log('Creating claim with data:', claimData);
      
      const response = await axios.post(`${API_URL}/claims`, claimData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const claimId = response.data.id;
      console.log('Claim created with ID:', claimId);

      // Upload photos du véhicule entreprise
      // Upload photos des véhicules accidentés
      console.log(`Uploading ${photosTiers.length} photos...`);
      toast.info('Préparation des photos...');
      
      for (let i = 0; i < photosTiers.length; i++) {
        const photo = photosTiers[i];
        try {
          // Compress image before upload
          const compressedFile = await compressImage(photo.file);
          
          const formData = new FormData();
          formData.append('file', compressedFile);
          formData.append('document_type', 'photo_damage');

          toast.info(`Envoi photo ${i + 1}/${photosTiers.length}...`);
          await axios.post(`${API_URL}/claims/${claimId}/documents`, formData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            },
            timeout: 60000 // 60 seconds timeout
          });
          console.log(`Photo ${i + 1}/${photosTiers.length} uploaded`);
        } catch (uploadErr) {
          console.error(`Error uploading photo ${i + 1}:`, uploadErr);
          throw new Error(`Erreur lors du téléchargement de la photo ${i + 1}. Veuillez réessayer.`);
        }
      }

      // Generate estimate (optional)
      try {
        console.log('Generating estimate...');
        await axios.post(`${API_URL}/claims/${claimId}/estimate`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Estimate generated');
      } catch (err) {
        console.log('Estimate generation optional:', err.message);
      }

      // Submit claim
      console.log('Submitting claim...');
      await axios.post(`${API_URL}/claims/${claimId}/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Claim submitted successfully!');
      toast.success('Dossier créé avec succès !');
      navigate(`/claims/${claimId}`);

    } catch (err) {
      console.error('Error in handleSubmit:', err);
      
      // Handle custom error messages
      if (err.message && !err.response) {
        setError(err.message);
        return;
      }
      
      // Handle API validation errors (array of objects) or string errors
      const errorDetail = err.response?.data?.detail;
      let errorMessage = 'Erreur lors de la création du dossier';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // Pydantic validation errors
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const progress = (currentStep / 5) * 100;

  return (
    <div className="max-w-2xl mx-auto pb-24 lg:pb-8" data-testid="new-claim">
      {/* Progress Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-xl md:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Nouveau dossier
          </h1>
          <span className="text-sm text-slate-500">Étape {currentStep}/5</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Step Indicator */}
      <div className="hidden md:flex justify-between mb-8">
        {STEPS.map((step) => {
          const StepIcon = step.icon;
          const isActive = step.id === currentStep;
          const isCompleted = step.id < currentStep;
          
          return (
            <div 
              key={step.id} 
              className={`flex flex-col items-center gap-2 ${isActive ? 'text-slate-900' : isCompleted ? 'text-emerald-600' : 'text-slate-400'}`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                isActive ? 'bg-slate-900 text-white' : 
                isCompleted ? 'bg-emerald-100 text-emerald-600' : 
                'bg-slate-100'
              }`}>
                {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
              </div>
              <span className="text-xs font-medium">{step.title}</span>
            </div>
          );
        })}
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-6" data-testid="error-alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Step Content */}
      <Card className="border-slate-100 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {(() => {
              const StepIcon = STEPS[currentStep - 1].icon;
              return <StepIcon className="w-5 h-5 text-amber-500" />;
            })()}
            {STEPS[currentStep - 1].title}
          </CardTitle>
          <CardDescription>{STEPS[currentStep - 1].description}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Step 1: Photos */}
          {currentStep === 1 && (
            <div className="space-y-6" data-testid="step-photos">
              {/* Photos véhicules accidentés */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Camera className="w-5 h-5 text-amber-500" />
                  <Label className="text-base font-semibold">Photos des véhicules accidentés *</Label>
                </div>
                
                {/* Inputs cachés */}
                <input
                  ref={fileInputTiersCameraRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoUploadTiers}
                  className="hidden"
                />
                <input
                  ref={fileInputTiersRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUploadTiers}
                  className="hidden"
                  data-testid="photo-input"
                />
                
                {/* Deux boutons : Caméra et Galerie */}
                <div className="grid grid-cols-2 gap-3">
                  <div 
                    className="border-2 border-dashed border-amber-200 bg-amber-50 rounded-xl p-4 text-center cursor-pointer hover:border-amber-400 hover:bg-amber-100 transition-colors active:bg-amber-200"
                    onClick={() => fileInputTiersCameraRef.current?.click()}
                  >
                    <Camera className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <p className="font-medium text-amber-800 text-sm">Prendre une photo</p>
                  </div>
                  <div 
                    className="border-2 border-dashed border-amber-200 bg-amber-50 rounded-xl p-4 text-center cursor-pointer hover:border-amber-400 hover:bg-amber-100 transition-colors active:bg-amber-200"
                    onClick={() => fileInputTiersRef.current?.click()}
                  >
                    <ImageIcon className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <p className="font-medium text-amber-800 text-sm">Galerie</p>
                  </div>
                </div>
                <p className="text-xs text-amber-600 text-center">
                  Photographiez les dommages et plaques d'immatriculation des deux véhicules
                </p>

                {photosTiers.length > 0 && (
                  <div className="grid grid-cols-3 gap-3">
                    {photosTiers.map((photo) => (
                      <div key={photo.id} className="relative aspect-square">
                        <img
                          src={photo.preview}
                          alt="Photo accident"
                          className="w-full h-full object-cover rounded-lg border-2 border-amber-200"
                        />
                        <button
                          onClick={() => removePhotoTiers(photo.id)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-sm text-amber-700">
                  {photosTiers.length} photo{photosTiers.length > 1 ? 's' : ''} ajoutée{photosTiers.length > 1 ? 's' : ''}
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Company Vehicle */}
          {currentStep === 2 && (
            <div className="space-y-4" data-testid="step-vehicle">
              <div className="space-y-2">
                <Label htmlFor="plate">Plaque d'immatriculation *</Label>
                <Input
                  id="plate"
                  placeholder="AA-123-BB ou AA123BB"
                  value={companyVehicle.plate}
                  onChange={(e) => {
                    setCompanyVehicle(prev => ({ ...prev, plate: e.target.value.toUpperCase() }));
                    setFieldErrors(prev => ({ ...prev, companyPlate: null }));
                  }}
                  className={`h-12 text-lg uppercase ${fieldErrors.companyPlate ? 'border-red-500 border-2' : ''}`}
                  data-testid="vehicle-plate-input"
                />
                {fieldErrors.companyPlate && (
                  <p className="text-red-500 text-sm">{fieldErrors.companyPlate}</p>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Third Party */}
          {currentStep === 3 && (
            <div className="space-y-4" data-testid="step-third-party">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tp-firstname">Prénom *</Label>
                  <Input
                    id="tp-firstname"
                    placeholder="Jean"
                    value={thirdParty.first_name}
                    onChange={(e) => {
                      setThirdParty(prev => ({ ...prev, first_name: e.target.value }));
                      setFieldErrors(prev => ({ ...prev, tpFirstName: null }));
                    }}
                    className={fieldErrors.tpFirstName ? 'border-red-500 border-2' : ''}
                    data-testid="tp-firstname-input"
                  />
                  {fieldErrors.tpFirstName && (
                    <p className="text-red-500 text-sm">{fieldErrors.tpFirstName}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tp-lastname">Nom *</Label>
                  <Input
                    id="tp-lastname"
                    placeholder="Dupont"
                    value={thirdParty.last_name}
                    onChange={(e) => {
                      setThirdParty(prev => ({ ...prev, last_name: e.target.value }));
                      setFieldErrors(prev => ({ ...prev, tpLastName: null }));
                    }}
                    className={fieldErrors.tpLastName ? 'border-red-500 border-2' : ''}
                    data-testid="tp-lastname-input"
                  />
                  {fieldErrors.tpLastName && (
                    <p className="text-red-500 text-sm">{fieldErrors.tpLastName}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tp-phone">Téléphone *</Label>
                <Input
                  id="tp-phone"
                  type="tel"
                  placeholder="+33 6 12 34 56 78"
                  value={thirdParty.phone}
                  onChange={(e) => {
                    setThirdParty(prev => ({ ...prev, phone: e.target.value }));
                    setFieldErrors(prev => ({ ...prev, tpPhone: null }));
                  }}
                  className={`h-12 ${fieldErrors.tpPhone ? 'border-red-500 border-2' : ''}`}
                  data-testid="tp-phone-input"
                />
                {fieldErrors.tpPhone && (
                  <p className="text-red-500 text-sm">{fieldErrors.tpPhone}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="tp-email">Email *</Label>
                <Input
                  id="tp-email"
                  type="email"
                  placeholder="jean.dupont@email.com"
                  value={thirdParty.email}
                  onChange={(e) => {
                    setThirdParty(prev => ({ ...prev, email: e.target.value }));
                    setFieldErrors(prev => ({ ...prev, tpEmail: null }));
                  }}
                  className={`h-12 ${fieldErrors.tpEmail ? 'border-red-500 border-2' : ''}`}
                  data-testid="tp-email-input"
                />
                {fieldErrors.tpEmail && (
                  <p className="text-red-500 text-sm">{fieldErrors.tpEmail}</p>
                )}
                <p className="text-xs text-slate-500">Obligatoire pour contacter le tiers</p>
              </div>
              <div className="pt-4 border-t">
                <Label className="text-base font-medium">Véhicule du tiers *</Label>
                <p className="text-xs text-slate-500 mb-3">Obligatoire pour vérification avec le devis</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Input
                      placeholder="Plaque *"
                      value={thirdParty.vehicle.plate}
                      onChange={(e) => {
                        setThirdParty(prev => ({ 
                          ...prev, 
                          vehicle: { ...prev.vehicle, plate: e.target.value.toUpperCase() } 
                        }));
                        setFieldErrors(prev => ({ ...prev, tpVehiclePlate: null }));
                      }}
                      className={`uppercase ${fieldErrors.tpVehiclePlate ? 'border-red-500 border-2' : ''}`}
                      data-testid="tp-vehicle-plate-input"
                    />
                    {fieldErrors.tpVehiclePlate && (
                      <p className="text-red-500 text-xs">{fieldErrors.tpVehiclePlate}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Incident */}
          {currentStep === 4 && (
            <div className="space-y-4" data-testid="step-incident">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="incident-date">Date *</Label>
                  <Input
                    id="incident-date"
                    type="date"
                    value={incident.date}
                    onChange={(e) => setIncident(prev => ({ ...prev, date: e.target.value }))}
                    className="h-12"
                    data-testid="incident-date-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="incident-city">Ville de l'accident *</Label>
                <Input
                  id="incident-city"
                  placeholder="Ex: Paris, Lyon, Marseille..."
                  value={incident.location.city}
                  onChange={(e) => {
                    setIncident(prev => ({ 
                      ...prev, 
                      location: { ...prev.location, city: e.target.value } 
                    }));
                    setFieldErrors(prev => ({ ...prev, incidentCity: null }));
                  }}
                  className={`h-12 ${fieldErrors.incidentCity ? 'border-red-500 border-2' : ''}`}
                  data-testid="incident-city-input"
                />
                {fieldErrors.incidentCity && (
                  <p className="text-red-500 text-sm">{fieldErrors.incidentCity}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="incident-description">Description de l'incident *</Label>
                <Textarea
                  id="incident-description"
                  placeholder="Décrivez comment l'incident s'est produit..."
                  value={incident.description}
                  onChange={(e) => {
                    setIncident(prev => ({ ...prev, description: e.target.value }));
                    setFieldErrors(prev => ({ ...prev, incidentDescription: null }));
                  }}
                  rows={4}
                  className={fieldErrors.incidentDescription ? 'border-red-500 border-2' : ''}
                  data-testid="incident-description-input"
                />
                {fieldErrors.incidentDescription && (
                  <p className="text-red-500 text-sm">{fieldErrors.incidentDescription}</p>
                )}
              </div>
              <div className="pt-4 border-t">
                <Label className="text-base font-medium">Adresse précise (optionnel)</Label>
                <div className="space-y-3 mt-3">
                  <Input
                    placeholder="Rue, numéro..."
                    value={incident.location.address}
                    onChange={(e) => setIncident(prev => ({ 
                      ...prev, 
                      location: { ...prev.location, address: e.target.value } 
                    }))}
                    data-testid="incident-address-input"
                  />
                  <Input
                    placeholder="Code postal"
                    value={incident.location.postal_code}
                    onChange={(e) => setIncident(prev => ({ 
                      ...prev, 
                      location: { ...prev.location, postal_code: e.target.value } 
                    }))}
                    data-testid="incident-postal-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes complémentaires</Label>
                <Textarea
                  id="notes"
                  placeholder="Informations supplémentaires..."
                  value={incident.notes}
                  onChange={(e) => setIncident(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  data-testid="incident-notes-input"
                />
              </div>
            </div>
          )}

          {/* Step 5: Confirmation */}
          {currentStep === 5 && (
            <div className="space-y-6" data-testid="step-confirmation">
              {/* Summary */}
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h4 className="font-medium text-slate-700 mb-2">Votre véhicule</h4>
                  <p className="text-slate-900">{companyVehicle.plate}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h4 className="font-medium text-slate-700 mb-2">Tiers identifié</h4>
                  <p className="text-slate-900">{thirdParty.first_name} {thirdParty.last_name}</p>
                  <p className="text-sm text-slate-600">{thirdParty.phone} - Véhicule: {thirdParty.vehicle.plate}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h4 className="font-medium text-slate-700 mb-2">Incident</h4>
                  <p className="text-sm text-slate-600">{incident.date}</p>
                  <p className="text-slate-900 mt-1">{incident.description}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h4 className="font-medium text-slate-700 mb-2">Photos</h4>
                  <p className="text-slate-900">
                    {photosTiers.length} photo{photosTiers.length > 1 ? 's' : ''} des véhicules accidentés
                  </p>
                </div>
              </div>

              {/* No Bodily Injury Confirmation */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="no-bodily-injury"
                    checked={noBodilyInjury}
                    onCheckedChange={setNoBodilyInjury}
                    className="mt-1"
                    data-testid="no-bodily-injury-checkbox"
                  />
                  <div>
                    <Label htmlFor="no-bodily-injury" className="text-amber-900 font-medium cursor-pointer">
                      Je confirme qu'aucun blessé ni dommage corporel n'a été constaté
                    </Label>
                    <p className="text-sm text-amber-700 mt-1">
                      Cette déclaration est obligatoire pour une procédure de résolution amiable.
                      En cas de doute sur un éventuel dommage corporel, veuillez contacter les secours.
                    </p>
                  </div>
                </div>
              </div>

              {/* Legal Notice */}
              <div className="p-4 bg-slate-100 rounded-xl flex items-start gap-3">
                <Shield className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-600">
                  En soumettant ce dossier, vous attestez de l'exactitude des informations fournies.
                  C2 AMIABLE intervient comme tiers de confiance pour faciliter la résolution amiable.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-6">
        {currentStep > 1 ? (
          <Button 
            variant="outline" 
            onClick={prevStep}
            className="h-12 px-6"
            data-testid="prev-step-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Précédent
          </Button>
        ) : (
          <div />
        )}

        {currentStep < 5 ? (
          <Button 
            onClick={nextStep}
            className="h-12 px-6 bg-slate-900 hover:bg-slate-800"
            data-testid="next-step-btn"
          >
            Suivant
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit}
            disabled={loading || !noBodilyInjury}
            className="h-12 px-6 bg-amber-500 hover:bg-amber-600"
            data-testid="submit-claim-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Soumettre le dossier
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default NewClaim;
